import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Mic, MicOff, Play, Pause, Trash2, Upload, Link as LinkIcon, Image, FileText, Send,
  Plus, X, Loader2, AlertCircle, CheckCircle, Clock, Globe, Smartphone, Film,
  Palette, ShoppingBag, Megaphone, Euro, Calculator, ListChecks, Users, ArrowRight,
  ArrowLeft, Save, Mail, Check, ChevronRight, Timer, Sparkles, Layout, Folder, 
  File, GripVertical, ChevronDown, Package, Settings, Calendar
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import DynamicBriefForm from '../../components/projects/DynamicBriefForm';

// Project types with their icons and specific fields
const PROJECT_TYPES = [
  { value: 'website', label: 'Site Web', icon: Globe, description: 'Site vitrine, e-commerce, blog...' },
  { value: 'mobile_app', label: 'Application Mobile', icon: Smartphone, description: 'iOS, Android ou hybride' },
  { value: 'video', label: 'Vidéo', icon: Film, description: 'Vidéo promotionnelle, corporate...' },
  { value: 'logo', label: 'Logo / Identité', icon: Palette, description: 'Logo, charte graphique...' },
  { value: 'ecommerce', label: 'E-commerce', icon: ShoppingBag, description: 'Boutique en ligne' },
  { value: 'marketing', label: 'Marketing / Pub', icon: Megaphone, description: 'Campagne pub, réseaux sociaux...' },
  { value: 'other', label: 'Autre', icon: FileText, description: 'Autre type de projet' },
  { value: 'express', label: '🚀 Intervention Express', icon: Timer, description: 'Demande rapide, brief simplifié' }
];

// Steps for the wizard
const STEPS = [
  { id: 1, title: 'Brief projet', icon: FileText, description: 'Informations de base du projet' },
  { id: 2, title: 'Client / Prospect', icon: Users, description: 'Sélectionner ou créer le contact' },
  { id: 3, title: 'Budgétisation', icon: Calculator, description: 'Estimation du temps et budget' },
  { id: 4, title: 'Validation', icon: CheckCircle, description: 'Récapitulatif et envoi' }
];

// Simplified steps for EXPRESS projects
const EXPRESS_STEPS = [
  { id: 1, title: 'Demande Express', icon: Timer, description: 'Titre et description de la demande' },
  { id: 2, title: 'Client', icon: Users, description: 'Sélectionner le client' },
  { id: 3, title: 'Estimation', icon: Calculator, description: 'Estimation rapide' },
  { id: 4, title: 'Envoi', icon: CheckCircle, description: 'Envoyer au client' }
];

const AgencyNewProjectPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const isSubmittingRef = useRef(false);  // Prevent double submission
  
  // Clients list
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [searchClient, setSearchClient] = useState('');
  
  // Form state - Step 1 (Brief)
  const [title, setTitle] = useState('');
  const [projectType, setProjectType] = useState('');
  const [description, setDescription] = useState('');
  const [inspirationLinks, setInspirationLinks] = useState(['']);
  const [images, setImages] = useState([]);
  const [deadlinePreference, setDeadlinePreference] = useState('');
  
  // Dynamic brief form data (from template)
  const [dynamicBriefData, setDynamicBriefData] = useState({});
  const [useDynamicForm, setUseDynamicForm] = useState(true);
  
  // Site structure (for website/ecommerce projects) - fallback if no template
  const [sitePages, setSitePages] = useState([
    { name: 'Accueil', level: 0, children: [] },
    { name: 'À propos', level: 0, children: [] },
    { name: 'Services', level: 0, children: [] },
    { name: 'Contact', level: 0, children: [] }
  ]);
  const [newPageName, setNewPageName] = useState('');
  const [selectedParentPage, setSelectedParentPage] = useState(null);
  
  // Expected deliverables - fallback if no template
  const [deliverables, setDeliverables] = useState([
    { id: 'maquettes', label: 'Maquettes graphiques', checked: true },
    { id: 'responsive', label: 'Version responsive (mobile/tablette)', checked: true },
    { id: 'sources', label: 'Fichiers sources', checked: false },
    { id: 'charte', label: 'Charte graphique', checked: false },
    { id: 'formation', label: 'Formation utilisation', checked: false }
  ]);
  const [newDeliverable, setNewDeliverable] = useState('');
  
  // Add deliverable function
  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setDeliverables([...deliverables, { 
        id: `custom_${Date.now()}`, 
        label: newDeliverable.trim(), 
        checked: true 
      }]);
      setNewDeliverable('');
    }
  };
  
  const toggleDeliverable = (id) => {
    setDeliverables(deliverables.map(d => 
      d.id === id ? { ...d, checked: !d.checked } : d
    ));
  };
  
  const removeDeliverable = (id) => {
    setDeliverables(deliverables.filter(d => d.id !== id));
  };
  
  // Site pages functions
  const addPage = (parentIndex = null) => {
    if (parentIndex !== null) {
      // Add subpage
      const newPages = [...sitePages];
      if (!newPages[parentIndex].children) {
        newPages[parentIndex].children = [];
      }
      newPages[parentIndex].children.push({
        name: 'Nouvelle sous-page',
        level: 1
      });
      setSitePages(newPages);
    } else {
      // Add main page
      setSitePages([...sitePages, { name: 'Nouvelle page', level: 0, children: [] }]);
    }
  };
  
  const updatePage = (index, name, subIndex = null) => {
    const newPages = [...sitePages];
    if (subIndex !== null) {
      newPages[index].children[subIndex].name = name;
    } else {
      newPages[index].name = name;
    }
    setSitePages(newPages);
  };
  
  const removePage = (index, subIndex = null) => {
    if (subIndex !== null) {
      const newPages = [...sitePages];
      newPages[index].children.splice(subIndex, 1);
      setSitePages(newPages);
    } else {
      setSitePages(sitePages.filter((_, i) => i !== index));
    }
  };
  
  // Project-specific fields
  const [projectSpecifics, setProjectSpecifics] = useState({
    // Website/Ecommerce
    hasExistingSite: false,
    existingSiteUrl: '',
    needsHosting: false,
    needsDomain: false,
    targetAudience: '',
    mainObjective: '',
    // Mobile app
    platforms: [], // ['ios', 'android']
    hasBackend: false,
    // Video
    videoDuration: '',
    videoStyle: '', // 'corporate', 'promotional', 'tutorial', 'animation'
    needsVoiceover: false,
    needsSubtitles: false,
    // Logo
    hasExistingLogo: false,
    colorPreferences: '',
    stylePreference: '', // 'modern', 'classic', 'minimalist', 'playful'
    // Marketing
    campaignType: '', // 'social', 'print', 'digital', 'mixed'
    targetPlatforms: [], // ['facebook', 'instagram', 'linkedin', 'google']
  });
  
  // Form state - Step 2 (Client)
  const [selectedClient, setSelectedClient] = useState(null);
  const [createNewClient, setCreateNewClient] = useState(false);
  const [newClientData, setNewClientData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    send_email: true
  });
  
  // Form state - Step 3 (Budgétisation)
  const [budgetMode, setBudgetMode] = useState('global'); // 'global' or 'tasks'
  const [hourlyRate, setHourlyRate] = useState(90);
  const [globalHours, setGlobalHours] = useState('');
  const [expressAmount, setExpressAmount] = useState(''); // Direct amount for EXPRESS projects
  const [tasks, setTasks] = useState([
    { name: 'Conception / Maquettes', hours: '', description: '' },
    { name: 'Développement', hours: '', description: '' },
    { name: 'Intégration', hours: '', description: '' },
    { name: 'Tests & Recette', hours: '', description: '' }
  ]);
  const [includeDiscount, setIncludeDiscount] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(10);
  
  // Payment schedule state
  const [usePaymentSchedule, setUsePaymentSchedule] = useState(true);
  const [scheduleType, setScheduleType] = useState('3'); // Number of installments
  const [depositPercent, setDepositPercent] = useState(30); // First payment percentage
  
  // Audio state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioTranscript, setAudioTranscript] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Fetch clients AND prospects
  // State for rate tiers
  const [rateTiers, setRateTiers] = useState([]);
  const [expressPricingMode, setExpressPricingMode] = useState('fixed'); // 'fixed' or 'hourly'
  const [expressHours, setExpressHours] = useState('');
  const [currentTierInfo, setCurrentTierInfo] = useState(null); // Info about current tier and savings

  // Helper function to find the applicable tier based on hours
  const findApplicableTier = useCallback((hours, tiers) => {
    if (!tiers || tiers.length === 0) return null;
    
    // Find the tier that applies to this number of hours
    let applicableTier = tiers[0];
    let nextTier = null;
    
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      const minHours = tier.min_hours || 0;
      const maxHours = tier.max_hours;
      
      if (hours >= minHours && (maxHours === null || hours < maxHours)) {
        applicableTier = tier;
        nextTier = i + 1 < tiers.length ? tiers[i + 1] : null;
        break;
      }
      // If hours exceed all tiers, use the last tier
      if (maxHours === null || hours >= maxHours) {
        applicableTier = tier;
        nextTier = i + 1 < tiers.length ? tiers[i + 1] : null;
      }
    }
    
    // Calculate savings compared to base rate (first tier)
    const baseRate = tiers[0]?.rate || 120;
    const currentRate = applicableTier?.rate || baseRate;
    const savings = hours > 0 ? (baseRate - currentRate) * hours : 0;
    
    return {
      tier: applicableTier,
      rate: currentRate,
      label: applicableTier?.label || '',
      nextTier,
      hoursToNextTier: nextTier ? (nextTier.min_hours - hours) : null,
      savings: savings > 0 ? savings : 0,
      baseRate
    };
  }, []);

  // Update hourly rate when hours change (for tiered pricing)
  useEffect(() => {
    if (rateTiers.length === 0) return;
    
    let totalHours = 0;
    if (projectType === 'express' && expressPricingMode === 'hourly') {
      totalHours = parseFloat(expressHours) || 0;
    } else if (projectType !== 'express') {
      if (budgetMode === 'global') {
        totalHours = parseFloat(globalHours) || 0;
      } else {
        totalHours = tasks.reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
      }
    }
    
    if (totalHours > 0) {
      const tierInfo = findApplicableTier(totalHours, rateTiers);
      if (tierInfo) {
        setHourlyRate(tierInfo.rate);
        setCurrentTierInfo(tierInfo);
      }
    } else {
      // Reset to first tier rate when no hours
      setHourlyRate(rateTiers[0]?.rate || 90);
      setCurrentTierInfo(null);
    }
  }, [rateTiers, globalHours, expressHours, tasks, budgetMode, projectType, expressPricingMode, findApplicableTier]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingClients(true);
      try {
        // Fetch clients, prospects, and rate tiers in parallel
        const [clientsRes, prospectsRes, ratesRes] = await Promise.all([
          api.get('/users/', { params: { role: 'client' } }),
          api.get('/prospects/').catch(() => ({ data: [] })),
          api.get('/settings/hourly-rates').catch(() => ({ data: { tiers: [] } }))
        ]);
        
        // Combine and mark the type
        const clientsList = (clientsRes.data || []).map(c => ({ ...c, contact_type: 'client' }));
        const prospectsList = (prospectsRes.data || []).map(p => ({ 
          ...p, 
          contact_type: 'prospect',
          id: p.id,
          first_name: p.first_name || p.contact_name?.split(' ')[0] || '',
          last_name: p.last_name || p.contact_name?.split(' ').slice(1).join(' ') || '',
          email: p.email || p.contact_email,
          company_name: p.company_name || p.company
        }));
        
        setClients([...clientsList, ...prospectsList]);
        
        // Set rate tiers and default hourly rate
        const tiers = ratesRes.data?.tiers || [];
        setRateTiers(tiers);
        
        // Set default hourly rate from first tier (highest rate for low volume)
        if (tiers.length > 0) {
          setHourlyRate(tiers[0].rate || tiers[0].hourly_rate || 90);
        }
      } catch (e) {
        console.error('Error fetching data:', e);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchData();
  }, []);

  // Calculate budget
  const calculateBudget = () => {
    // For EXPRESS projects with FIXED pricing, use direct amount
    if (projectType === 'express' && expressPricingMode === 'fixed') {
      const directAmount = parseFloat(expressAmount) || 0;
      const discountAmount = includeDiscount ? directAmount * (discountPercent / 100) : 0;
      const netAmount = directAmount - discountAmount;
      
      return {
        totalHours: 0, // No hours for fixed express
        grossAmount: directAmount,
        discountAmount,
        netAmount,
        pricingMode: 'fixed'
      };
    }
    
    // For EXPRESS projects with HOURLY pricing
    if (projectType === 'express' && expressPricingMode === 'hourly') {
      const hours = parseFloat(expressHours) || 0;
      const grossAmount = hours * hourlyRate;
      const discountAmount = includeDiscount ? grossAmount * (discountPercent / 100) : 0;
      const netAmount = grossAmount - discountAmount;
      
      return {
        totalHours: hours,
        grossAmount,
        discountAmount,
        netAmount,
        pricingMode: 'hourly'
      };
    }
    
    // Standard calculation based on hours
    let totalHours = 0;
    
    if (budgetMode === 'global') {
      totalHours = parseFloat(globalHours) || 0;
    } else {
      totalHours = tasks.reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
    }
    
    const grossAmount = totalHours * hourlyRate;
    const discountAmount = includeDiscount ? grossAmount * (discountPercent / 100) : 0;
    const netAmount = grossAmount - discountAmount;
    
    return {
      totalHours,
      grossAmount,
      discountAmount,
      netAmount,
      pricingMode: 'hourly'
    };
  };

  const budget = calculateBudget();

  // Audio recording functions
  const startRecording = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Votre navigateur ne supporte pas l\'enregistrement audio. Utilisez Chrome, Firefox ou Edge.');
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        await transcribeAudio(blob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success('Enregistrement démarré');
    } catch (error) {
      console.error('Microphone error:', error);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('Aucun microphone détecté. Vérifiez que votre micro est bien connecté.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Le microphone est utilisé par une autre application. Fermez les autres applications qui l\'utilisent.');
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Erreur de configuration du microphone.');
      } else if (error.name === 'TypeError') {
        toast.error('Erreur technique. Assurez-vous que la page est en HTTPS.');
      } else {
        toast.error(`Impossible d'accéder au microphone: ${error.message || 'Erreur inconnue'}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      const response = await api.post('/briefs/transcribe-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAudioTranscript(response.data.transcription || response.data.transcript || '');
      toast.success('Audio transcrit avec succès !');
    } catch (error) {
      toast.error('Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  const deleteAudio = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioTranscript('');
  };

  // Link management
  const addLink = () => setInspirationLinks([...inspirationLinks, '']);
  const updateLink = (index, value) => {
    const newLinks = [...inspirationLinks];
    newLinks[index] = value;
    setInspirationLinks(newLinks);
  };
  const removeLink = (index) => setInspirationLinks(inspirationLinks.filter((_, i) => i !== index));

  // Task management
  const addTask = () => setTasks([...tasks, { name: '', hours: '', description: '' }]);
  const updateTask = (index, field, value) => {
    const newTasks = [...tasks];
    newTasks[index][field] = value;
    setTasks(newTasks);
  };
  const removeTask = (index) => setTasks(tasks.filter((_, i) => i !== index));

  // Image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => setImages(prev => [...prev, e.target.result]);
      reader.readAsDataURL(file);
    });
  };
  const removeImage = (index) => setImages(images.filter((_, i) => i !== index));

  // Validation
  const validateStep = (step) => {
    if (step === 1) {
      if (!title.trim()) { toast.error('Veuillez donner un titre au projet'); return false; }
      if (!projectType) { toast.error('Veuillez sélectionner un type de projet'); return false; }
      
      // For EXPRESS projects, only title is required (description is optional)
      if (projectType === 'express') {
        return true;
      }
      
      // Check description from dynamic form OR old fields
      const dynamicDescription = dynamicBriefData?.description;
      if (!description.trim() && !audioTranscript && !dynamicDescription) { 
        toast.error('Veuillez décrire le projet'); 
        return false; 
      }
    }
    if (step === 2) {
      if (!selectedClient && !createNewClient) { toast.error('Veuillez sélectionner un client ou prospect'); return false; }
      if (createNewClient) {
        if (!newClientData.company_name && !newClientData.first_name) { toast.error('Nom ou société requis'); return false; }
        if (!newClientData.email) { toast.error('Email client requis'); return false; }
      }
    }
    if (step === 3) {
      // For EXPRESS projects, validation depends on pricing mode
      if (projectType === 'express') {
        if (expressPricingMode === 'fixed') {
          if (!expressAmount || parseFloat(expressAmount) <= 0) {
            toast.error('Veuillez indiquer le montant de l\'intervention');
            return false;
          }
        } else {
          // Hourly mode
          if (!expressHours || parseFloat(expressHours) <= 0) {
            toast.error('Veuillez indiquer le nombre d\'heures');
            return false;
          }
        }
      } else {
        if (budgetMode === 'global' && !globalHours) { toast.error('Veuillez estimer le temps global'); return false; }
        if (budgetMode === 'tasks' && tasks.every(t => !t.hours)) { toast.error('Veuillez estimer au moins une tâche'); return false; }
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  // Submit - Create project and send to client
  const handleSubmit = async (sendToClient = false) => {
    if (!validateStep(3)) return;
    
    // Prevent double submission
    if (isSubmittingRef.current || saving) {
      console.log('Submission already in progress, ignoring duplicate request');
      return;
    }
    isSubmittingRef.current = true;
    setSaving(true);
    try {
      // Create client if new
      let clientId = selectedClient?.id;
      let isNewClient = false;
      
      if (createNewClient) {
        const clientRes = await api.post('/users/clients', {
          ...newClientData,
          send_email: newClientData.send_email !== false
        });
        clientId = clientRes.data.id;
        isNewClient = true;
        toast.success('Client créé - Accès envoyés par email');
      } else if (selectedClient?.contact_type === 'prospect') {
        // Convert prospect to client
        const prospectData = {
          email: selectedClient.email,
          first_name: selectedClient.first_name,
          last_name: selectedClient.last_name,
          company_name: selectedClient.company_name,
          phone: selectedClient.phone,
          send_email: true
        };
        const clientRes = await api.post('/users/clients', prospectData);
        clientId = clientRes.data.id;
        isNewClient = true;
        toast.success('Prospect converti en client - Accès envoyés');
      }

      // Build tasks array for budget
      const budgetTasks = budgetMode === 'tasks' 
        ? tasks.filter(t => t.name && t.hours).map(t => ({
            name: t.name,
            hours: parseFloat(t.hours),
            description: t.description,
            amount: parseFloat(t.hours) * hourlyRate
          }))
        : [];

      // Build brief data from dynamic form
      // Extract site_structure and deliverables from dynamic form data if present
      const siteStructureFromDynamic = dynamicBriefData.site_structure || dynamicBriefData.sitemap;
      const deliverablesFromDynamic = dynamicBriefData.deliverables;
      
      const briefData = {
        project_type: projectType,
        description: dynamicBriefData.description || description || audioTranscript,
        inspiration_links: dynamicBriefData.inspiration_links || inspirationLinks.filter(l => l.trim()),
        // Site structure from dynamic form or fallback
        site_structure: siteStructureFromDynamic ? {
          pages: Array.isArray(siteStructureFromDynamic) ? siteStructureFromDynamic : sitePages,
          total_pages: Array.isArray(siteStructureFromDynamic) 
            ? siteStructureFromDynamic.reduce((acc, p) => acc + 1 + (p.children?.length || 0), 0)
            : sitePages.reduce((acc, p) => acc + 1 + (p.children?.length || 0), 0)
        } : null,
        // Deliverables from dynamic form or fallback
        deliverables: deliverablesFromDynamic 
          ? deliverablesFromDynamic.map(d => ({ id: d.id, label: d.label }))
          : deliverables.filter(d => d.checked).map(d => ({ id: d.id, label: d.label })),
        // All other dynamic form fields
        ...Object.fromEntries(
          Object.entries(dynamicBriefData).filter(([key]) => 
            !['description', 'inspiration_links', 'site_structure', 'sitemap', 'deliverables'].includes(key)
          )
        ),
        // Project specifics (legacy)
        specifics: projectSpecifics
      };

      // Build payment schedule data
      const scheduleData = usePaymentSchedule ? {
        enabled: true,
        installments: parseInt(scheduleType),
        deposit_percent: depositPercent,
        total_amount: budget.netAmount
      } : null;

      // Upload audio brief if present
      let uploadedAudioUrl = null;
      if (audioBlob) {
        try {
          const audioFormData = new FormData();
          audioFormData.append('file', audioBlob, 'brief_audio.webm');
          const uploadRes = await api.post('/upload', audioFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedAudioUrl = uploadRes.data.url || uploadRes.data.file_url;
        } catch (uploadError) {
          console.error('Error uploading audio:', uploadError);
          // Continue without audio if upload fails
        }
      }

      // Create project
      const projectData = {
        name: title,
        project_type: projectType,
        description: projectType === 'express' 
          ? (dynamicBriefData.express_description || description || audioTranscript || 'Intervention Express')
          : (dynamicBriefData.description || description || audioTranscript),
        client_id: clientId,
        deadline: deadlinePreference || null,
        status: sendToClient ? 'pending_signature' : 'draft',
        brief_data: briefData,
        brief_audio_url: uploadedAudioUrl,
        brief_audio_transcript: audioTranscript || null,
        is_express: projectType === 'express',
        express_pricing_mode: projectType === 'express' ? expressPricingMode : null,
        tags: projectType === 'express' ? ['EXPRESS'] : [],
        budget_data: {
          mode: projectType === 'express' ? expressPricingMode : budgetMode,
          hourly_rate: hourlyRate,
          total_hours: budget.totalHours,
          gross_amount: budget.grossAmount,
          discount_percent: includeDiscount ? discountPercent : 0,
          discount_amount: budget.discountAmount,
          net_amount: budget.netAmount,
          tasks: budgetTasks,
          pricing_mode: budget.pricingMode,
          tier_info: currentTierInfo ? {
            label: currentTierInfo.label,
            rate: currentTierInfo.rate,
            savings: currentTierInfo.savings
          } : null
        },
        payment_schedule: scheduleData,
        inspiration_links: dynamicBriefData.inspiration_links || inspirationLinks.filter(l => l.trim()),
        images: images,
        created_by: user?.id,
        send_to_client: sendToClient
      };

      const response = await api.post('/projects/', projectData);
      const projectId = response.data.id;
      
      // Create payment schedule if enabled and sending to client
      if (sendToClient && usePaymentSchedule && budget.netAmount > 0) {
        try {
          await api.post(`/projects/${projectId}/payment-schedule`, {
            total_amount: budget.netAmount,
            installments: parseInt(scheduleType),
            deposit_percent: depositPercent
          });
        } catch (scheduleError) {
          console.error('Error creating payment schedule:', scheduleError);
          // Don't fail the whole operation
        }
      }
      
      if (sendToClient) {
        // Send budget/devis email to client
        try {
          await api.post(`/projects/${projectId}/send-budget-validation`);
        } catch (emailError) {
          console.error('Email send failed:', emailError);
        }
        toast.success('Projet créé et envoyé au client pour signature !');
      } else {
        toast.success('Projet enregistré en brouillon');
      }
      
      navigate(`/projects/${projectId}`);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
      isSubmittingRef.current = false;  // Reset the submission flag
    }
  };

  // Filter clients
  const filteredClients = clients.filter(c => 
    (c.company_name || '').toLowerCase().includes(searchClient.toLowerCase()) ||
    (c.first_name || '').toLowerCase().includes(searchClient.toLowerCase()) ||
    (c.last_name || '').toLowerCase().includes(searchClient.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(searchClient.toLowerCase())
  );

  const selectedTypeConfig = PROJECT_TYPES.find(t => t.value === projectType);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-4 md:px-8 pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
            Créer un nouveau projet
          </h1>
          <p className="text-slate-500 mt-1">
            Définissez le brief, budgétisez et envoyez au client pour validation
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {(projectType === 'express' ? EXPRESS_STEPS : STEPS).map((step, index) => {
              const stepsArray = projectType === 'express' ? EXPRESS_STEPS : STEPS;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              const Icon = step.icon;
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div 
                      className={cn(
                        "w-12 h-12 rounded-full flex items-center justify-center transition-all",
                        isActive ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg" :
                        isCompleted ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
                      )}
                    >
                      {isCompleted ? <Check className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={cn(
                      "mt-2 text-xs font-medium hidden sm:block",
                      isActive ? "text-cyan-600" : isCompleted ? "text-emerald-600" : "text-slate-500"
                    )}>
                      {step.title}
                    </span>
                  </div>
                  {index < stepsArray.length - 1 && (
                    <div className={cn(
                      "flex-1 h-1 mx-2 rounded",
                      currentStep > step.id ? "bg-emerald-500" : "bg-slate-200"
                    )} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Step 1: Brief */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Title */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cyan-600" />
                    Titre du projet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex: Refonte site web, Application mobile, Vidéo promotionnelle..."
                    className="text-lg"
                  />
                </CardContent>
              </Card>

              {/* Project Type */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-600" />
                    Type de projet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {PROJECT_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = projectType === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          data-testid={`project-type-${type.value}`}
                          onClick={() => setProjectType(type.value)}
                          className={cn(
                            "p-4 rounded-xl border-2 transition-all text-left",
                            isSelected ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-slate-300"
                          )}
                        >
                          <Icon className={cn("w-6 h-6 mb-2", isSelected ? "text-cyan-600" : "text-slate-400")} />
                          <p className={cn("font-medium text-sm", isSelected ? "text-cyan-700" : "text-slate-700")}>
                            {type.label}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Dynamic Brief Form based on project type */}
              {projectType && (
                <>
                  {/* Audio Recording for Description */}
                  <Card className="border-0 shadow-sm border-l-4 border-l-violet-500">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Mic className="w-5 h-5 text-violet-600" />
                        Brief audio (optionnel)
                      </CardTitle>
                      <CardDescription>
                        Décrivez votre projet à l&apos;oral, nous le transcrirons automatiquement
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                        <Button
                          type="button"
                          variant={isRecording ? "destructive" : "outline"}
                          size="lg"
                          onClick={isRecording ? stopRecording : startRecording}
                          className="gap-2"
                        >
                          {isRecording ? (
                            <>
                              <MicOff className="w-5 h-5" />
                              Arrêter l&apos;enregistrement
                            </>
                          ) : (
                            <>
                              <Mic className="w-5 h-5" />
                              Commencer l&apos;enregistrement
                            </>
                          )}
                        </Button>
                        
                        {isRecording && (
                          <div className="flex items-center gap-2 text-red-500">
                            <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                            <span className="text-sm font-medium">Enregistrement en cours...</span>
                          </div>
                        )}
                      </div>
                      
                      {audioUrl && (
                        <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                          <audio src={audioUrl} controls className="flex-1 h-10" />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon"
                            onClick={() => {
                              URL.revokeObjectURL(audioUrl);
                              setAudioUrl(null);
                              setAudioBlob(null);
                              setAudioTranscript('');
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {isTranscribing && (
                        <div className="flex items-center gap-2 text-slate-500 p-3 bg-blue-50 rounded-lg">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Transcription en cours...</span>
                        </div>
                      )}
                      
                      {audioTranscript && (
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                          <p className="text-sm font-medium text-emerald-700 mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Transcription :
                          </p>
                          <p className="text-sm text-emerald-800">{audioTranscript}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Link to manage templates */}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/agency/brief-templates')}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Gérer les templates
                    </Button>
                  </div>
                  
                  {/* Dynamic form fields - NOT for EXPRESS */}
                  {projectType !== 'express' && (
                    <DynamicBriefForm
                      projectType={projectType}
                      values={dynamicBriefData}
                      onChange={setDynamicBriefData}
                      isAgency={true}
                      onVoiceInput={(fieldId) => {
                        // Could trigger voice input for specific field
                        toast.info('Dictée vocale - Cliquez sur le micro principal pour enregistrer');
                      }}
                    />
                  )}
                  
                  {/* Simplified form for EXPRESS */}
                  {projectType === 'express' && (
                    <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Timer className="w-5 h-5 text-amber-600" />
                          Description de la demande Express
                        </CardTitle>
                        <CardDescription>
                          Décrivez simplement ce que vous devez faire pour le client
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          placeholder="Ex: Mise à jour urgente du site, correction d'un bug, modification de texte, ajout d'une page..."
                          value={dynamicBriefData.express_description || ''}
                          onChange={(e) => setDynamicBriefData({...dynamicBriefData, express_description: e.target.value})}
                          rows={5}
                          className="resize-none"
                        />
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* Deadline - always show */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-600" />
                    Délai souhaité
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    type="date"
                    value={deadlinePreference}
                    onChange={(e) => setDeadlinePreference(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Client Selection */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Sélectionner le client ou prospect
                  </CardTitle>
                  <CardDescription>Choisissez un client/prospect existant ou créez-en un nouveau</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Toggle */}
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Switch checked={createNewClient} onCheckedChange={setCreateNewClient} />
                    <Label>Créer un nouveau client</Label>
                  </div>

                  {!createNewClient ? (
                    <>
                      {/* Search */}
                      <Input
                        placeholder="Rechercher un client ou prospect..."
                        value={searchClient}
                        onChange={(e) => setSearchClient(e.target.value)}
                      />
                      
                      {/* Clients list */}
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {loadingClients ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                          </div>
                        ) : filteredClients.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>Aucun client ou prospect trouvé</p>
                          </div>
                        ) : (
                          filteredClients.map((client) => (
                            <div
                              key={client.id}
                              onClick={() => setSelectedClient(client)}
                              className={cn(
                                "p-3 rounded-lg border-2 cursor-pointer transition-all",
                                selectedClient?.id === client.id 
                                  ? "border-cyan-500 bg-cyan-50" 
                                  : "border-slate-200 hover:border-slate-300"
                              )}
                            >
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{client.company_name || `${client.first_name} ${client.last_name}`}</p>
                                <Badge 
                                  variant={client.contact_type === 'prospect' ? 'outline' : 'default'}
                                  className={cn(
                                    "text-xs",
                                    client.contact_type === 'prospect' 
                                      ? "border-orange-300 text-orange-600 bg-orange-50" 
                                      : "bg-emerald-100 text-emerald-700"
                                  )}
                                >
                                  {client.contact_type === 'prospect' ? 'Prospect' : 'Client'}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-500">{client.email}</p>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  ) : (
                    /* New client form */
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Nom de la société</Label>
                        <Input
                          value={newClientData.company_name}
                          onChange={(e) => setNewClientData({...newClientData, company_name: e.target.value})}
                          placeholder="Nom de l'entreprise"
                        />
                      </div>
                      <div>
                        <Label>Prénom</Label>
                        <Input
                          value={newClientData.first_name}
                          onChange={(e) => setNewClientData({...newClientData, first_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Nom</Label>
                        <Input
                          value={newClientData.last_name}
                          onChange={(e) => setNewClientData({...newClientData, last_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={newClientData.email}
                          onChange={(e) => setNewClientData({...newClientData, email: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        <Input
                          value={newClientData.phone}
                          onChange={(e) => setNewClientData({...newClientData, phone: e.target.value})}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Budgétisation */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Budget Mode Selection */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-emerald-600" />
                    Mode de budgétisation
                  </CardTitle>
                  <CardDescription>Choisissez comment estimer le budget du projet</CardDescription>
                </CardHeader>
                <CardContent>
                  {projectType !== 'express' ? (
                    <Tabs value={budgetMode} onValueChange={setBudgetMode}>
                      <TabsList className="grid grid-cols-2 w-full">
                        <TabsTrigger value="global" className="gap-2">
                          <Timer className="w-4 h-4" />
                          Temps global
                        </TabsTrigger>
                        <TabsTrigger value="tasks" className="gap-2">
                          <ListChecks className="w-4 h-4" />
                          Par tâche
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  ) : (
                    /* Express pricing mode selection */
                    <div className="space-y-3">
                      <Tabs value={expressPricingMode} onValueChange={setExpressPricingMode}>
                        <TabsList className="grid grid-cols-2 w-full">
                          <TabsTrigger value="fixed" className="gap-2" data-testid="express-mode-fixed">
                            <Euro className="w-4 h-4" />
                            Forfait fixe
                          </TabsTrigger>
                          <TabsTrigger value="hourly" className="gap-2" data-testid="express-mode-hourly">
                            <Timer className="w-4 h-4" />
                            À l&apos;heure
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                      <p className="text-xs text-slate-500">
                        {expressPricingMode === 'fixed' 
                          ? '💰 Montant fixe convenu avec le client'
                          : '⏱️ Facturation selon le nombre d\'heures (tarif dégressif)'
                        }
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* For EXPRESS with FIXED pricing: Direct amount input */}
              {projectType === 'express' && expressPricingMode === 'fixed' && (
                <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Euro className="w-5 h-5 text-amber-600" />
                      Montant forfaitaire
                    </CardTitle>
                    <CardDescription>Entrez le prix total HT pour cette intervention</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Input
                        type="text"
                        inputMode="decimal"
                        data-testid="express-amount-input"
                        value={expressAmount}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                          setExpressAmount(val);
                        }}
                        placeholder="Ex: 150"
                        className="w-40 text-lg"
                      />
                      <span className="text-slate-600 font-medium">€ HT</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      TTC : {((parseFloat(expressAmount) || 0) * 1.2).toLocaleString('fr-FR')}€
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* For EXPRESS with HOURLY pricing: Hours input with tiered rate */}
              {projectType === 'express' && expressPricingMode === 'hourly' && (
                <Card className="border-0 shadow-sm border-l-4 border-l-cyan-500">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Timer className="w-5 h-5 text-cyan-600" />
                      Nombre d&apos;heures
                    </CardTitle>
                    <CardDescription>Le taux horaire s&apos;ajuste automatiquement selon le volume</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Input
                        type="text"
                        inputMode="decimal"
                        data-testid="express-hours-input"
                        value={expressHours}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                          setExpressHours(val);
                        }}
                        placeholder="Ex: 5"
                        className="w-32 text-lg"
                      />
                      <span className="text-slate-500">heures</span>
                      <span className="text-slate-400">×</span>
                      <span className="font-bold text-cyan-600">{hourlyRate}€/h</span>
                    </div>
                    
                    {/* Current tier info */}
                    {currentTierInfo && (
                      <div className="p-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-cyan-800">
                            {currentTierInfo.label}
                          </span>
                          {currentTierInfo.savings > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">
                              Économie : {currentTierInfo.savings.toLocaleString('fr-FR')}€
                            </Badge>
                          )}
                        </div>
                        {currentTierInfo.nextTier && currentTierInfo.hoursToNextTier > 0 && (
                          <p className="text-xs text-cyan-600 mt-1">
                            💡 Encore {currentTierInfo.hoursToNextTier.toFixed(0)}h pour passer à {currentTierInfo.nextTier.rate}€/h
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Hourly Rate with tiered info - NOT for EXPRESS */}
              {projectType !== 'express' && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Euro className="w-5 h-5 text-amber-600" />
                    Taux horaire {currentTierInfo ? '(dégressif)' : ''}
                  </CardTitle>
                  {rateTiers.length > 0 && (
                    <CardDescription>
                      Le taux s&apos;ajuste automatiquement selon le volume d&apos;heures
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={hourlyRate}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                        setHourlyRate(parseFloat(val) || 0);
                      }}
                      className="w-32"
                    />
                    <span className="text-slate-500">€ HT / heure</span>
                  </div>
                  
                  {/* Current tier info */}
                  {currentTierInfo && (
                    <div className="p-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-cyan-800">
                          {currentTierInfo.label}
                        </span>
                        {currentTierInfo.savings > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0">
                            Économie : {currentTierInfo.savings.toLocaleString('fr-FR')}€
                          </Badge>
                        )}
                      </div>
                      {currentTierInfo.nextTier && currentTierInfo.hoursToNextTier > 0 && (
                        <p className="text-xs text-cyan-600 mt-1">
                          💡 Encore {currentTierInfo.hoursToNextTier.toFixed(0)}h pour passer à {currentTierInfo.nextTier.rate}€/h
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Rate tiers reference */}
                  {rateTiers.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                        Voir la grille tarifaire
                      </summary>
                      <div className="mt-2 p-2 bg-slate-50 rounded-lg space-y-1">
                        {rateTiers.map((tier, idx) => (
                          <div key={idx} className={cn(
                            "flex justify-between py-1 px-2 rounded",
                            currentTierInfo?.tier?.label === tier.label ? "bg-cyan-100 font-medium" : ""
                          )}>
                            <span>{tier.label}</span>
                            <span className="font-mono">{tier.rate}€/h</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Global Hours - NOT for EXPRESS */}
              {projectType !== 'express' && budgetMode === 'global' && (
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Timer className="w-5 h-5 text-blue-600" />
                      Estimation temps global
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Input
                        type="text"
                        inputMode="decimal"
                        data-testid="global-hours-input"
                        value={globalHours}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                          setGlobalHours(val);
                        }}
                        placeholder="Ex: 40"
                        className="w-32"
                      />
                      <span className="text-slate-500">heures</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tasks Breakdown - NOT for EXPRESS */}
              {projectType !== 'express' && budgetMode === 'tasks' && (
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ListChecks className="w-5 h-5 text-purple-600" />
                      Détail par tâche
                    </CardTitle>
                    <CardDescription>Estimez le temps pour chaque phase du projet</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {tasks.map((task, index) => (
                      <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                        <div className="flex items-center gap-3">
                          <Input
                            value={task.name}
                            onChange={(e) => updateTask(index, 'name', e.target.value)}
                            placeholder="Nom de la tâche"
                            className="flex-1"
                          />
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={task.hours}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                              updateTask(index, 'hours', val);
                            }}
                            placeholder="Heures"
                            className="w-24"
                          />
                          <span className="text-slate-500 text-sm">h</span>
                          {tasks.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeTask(index)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                        {task.hours && (
                          <div className="text-right text-sm text-emerald-600 font-medium">
                            = {(parseFloat(task.hours) * hourlyRate).toLocaleString('fr-FR')}€ HT
                          </div>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={addTask}>
                      <Plus className="w-4 h-4 mr-1" /> Ajouter une tâche
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Discount */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-pink-600" />
                      Remise commerciale
                    </CardTitle>
                    <Switch checked={includeDiscount} onCheckedChange={setIncludeDiscount} />
                  </div>
                </CardHeader>
                {includeDiscount && (
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={discountPercent}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                          setDiscountPercent(parseFloat(val) || 0);
                        }}
                        className="w-24"
                      />
                      <span className="text-slate-500">%</span>
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Budget Summary */}
              <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-800 to-slate-900 text-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    {projectType === 'express' 
                      ? (expressPricingMode === 'fixed' ? 'Montant forfaitaire' : 'Montant intervention')
                      : 'Récapitulatif budget'
                    }
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Show hours info for standard projects OR express hourly mode */}
                  {(projectType !== 'express' || expressPricingMode === 'hourly') && budget.totalHours > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Temps total</span>
                        <span className="font-bold">{budget.totalHours} heures</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Montant brut ({hourlyRate}€/h)</span>
                        <span>{budget.grossAmount.toLocaleString('fr-FR')}€ HT</span>
                      </div>
                      {/* Show savings for tiered pricing */}
                      {currentTierInfo?.savings > 0 && (
                        <div className="flex justify-between text-cyan-400">
                          <span>Économie tarif dégressif</span>
                          <span>-{currentTierInfo.savings.toLocaleString('fr-FR')}€</span>
                        </div>
                      )}
                    </>
                  )}
                  {/* For fixed express, show the amount directly */}
                  {projectType === 'express' && expressPricingMode === 'fixed' && (
                    <div className="flex justify-between">
                      <span className="text-slate-300">Montant forfait</span>
                      <span>{budget.grossAmount.toLocaleString('fr-FR')}€ HT</span>
                    </div>
                  )}
                  {includeDiscount && (
                    <div className="flex justify-between text-pink-400">
                      <span>Remise ({discountPercent}%)</span>
                      <span>-{budget.discountAmount.toLocaleString('fr-FR')}€</span>
                    </div>
                  )}
                  <div className="border-t border-slate-600 pt-3 flex justify-between">
                    <span className="font-bold text-lg">Total HT</span>
                    <span className="font-bold text-2xl text-emerald-400">
                      {budget.netAmount.toLocaleString('fr-FR')}€
                    </span>
                  </div>
                  <div className="flex justify-between text-slate-300 text-sm">
                    <span>TTC (20%)</span>
                    <span>{(budget.netAmount * 1.2).toLocaleString('fr-FR')}€</span>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Schedule - NOT for EXPRESS */}
              {projectType !== 'express' && (
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Timer className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                      Échéancier de paiement
                    </CardTitle>
                    <Switch checked={usePaymentSchedule} onCheckedChange={setUsePaymentSchedule} />
                  </div>
                  <CardDescription>
                    Proposer un paiement échelonné au client
                  </CardDescription>
                </CardHeader>
                {usePaymentSchedule && budget.netAmount > 0 && (
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Nombre d&apos;échéances</Label>
                        <Select value={scheduleType} onValueChange={setScheduleType}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2 échéances</SelectItem>
                            <SelectItem value="3">3 échéances</SelectItem>
                            <SelectItem value="4">4 échéances</SelectItem>
                            <SelectItem value="6">6 échéances</SelectItem>
                            <SelectItem value="12">12 échéances</SelectItem>
                            <SelectItem value="18">18 échéances</SelectItem>
                            <SelectItem value="24">24 échéances</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Acompte initial (%)</Label>
                        <Select value={depositPercent.toString()} onValueChange={(v) => setDepositPercent(parseInt(v))}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20%</SelectItem>
                            <SelectItem value="30">30%</SelectItem>
                            <SelectItem value="40">40%</SelectItem>
                            <SelectItem value="50">50%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Schedule Preview */}
                    <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                      <p className="text-sm font-medium text-slate-700 mb-3">Aperçu de l&apos;échéancier :</p>
                      {(() => {
                        const installments = parseInt(scheduleType);
                        const deposit = (budget.netAmount * depositPercent) / 100;
                        const remaining = budget.netAmount - deposit;
                        const monthlyAmount = remaining / (installments - 1);
                        
                        return (
                          <div className="space-y-2">
                            <div className="flex justify-between items-center p-2 bg-white rounded border border-emerald-200">
                              <span className="text-sm">
                                <Badge variant="outline" className="mr-2 border-emerald-500 text-emerald-600">
                                  Acompte
                                </Badge>
                                À la signature
                              </span>
                              <span className="font-bold text-emerald-600">
                                {deposit.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€
                              </span>
                            </div>
                            {Array.from({ length: installments - 1 }).map((_, i) => (
                              <div key={i} className="flex justify-between items-center p-2 bg-white rounded border">
                                <span className="text-sm text-slate-600">
                                  Échéance {i + 1} - Mois {i + 1}
                                </span>
                                <span className="font-medium">
                                  {monthlyAmount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    
                    <p className="text-xs text-slate-500">
                      💡 Le client devra payer l&apos;acompte pour valider le projet. Les échéances seront ajoutées à son espace.
                    </p>
                  </CardContent>
                )}
              </Card>
              )}
            </div>
          )}

          {/* Step 4: Validation */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    Récapitulatif du projet
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Project Info */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      {selectedTypeConfig && <selectedTypeConfig.icon className="w-5 h-5 text-cyan-600" />}
                      <span className="font-bold text-lg" style={{ color: theme.colors?.primary }}>{title}</span>
                      {projectType === 'express' && (
                        <Badge className="bg-amber-500 text-white ml-2">🚀 EXPRESS</Badge>
                      )}
                    </div>
                    <Badge>{selectedTypeConfig?.label}</Badge>
                    <p className="text-sm text-slate-600 mt-2">
                      {projectType === 'express' 
                        ? (dynamicBriefData.express_description || description || audioTranscript || 'Intervention rapide')
                        : (description || audioTranscript)
                      }
                    </p>
                  </div>

                  {/* Client Info */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-700 mb-1">Client</p>
                    <p className="font-bold text-blue-900">
                      {createNewClient 
                        ? (newClientData.company_name || `${newClientData.first_name} ${newClientData.last_name}`)
                        : (selectedClient?.company_name || `${selectedClient?.first_name} ${selectedClient?.last_name}`)
                      }
                    </p>
                    <p className="text-sm text-blue-700">
                      {createNewClient ? newClientData.email : selectedClient?.email}
                    </p>
                  </div>

                  {/* Budget Summary */}
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <p className="text-sm font-medium text-emerald-700 mb-2">
                      {projectType === 'express' ? 'Montant intervention' : 'Budget proposé'}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {projectType !== 'express' && (
                        <>
                          <span className="text-slate-600">Temps estimé :</span>
                          <span className="font-medium">{budget.totalHours} heures</span>
                          <span className="text-slate-600">Taux horaire :</span>
                          <span className="font-medium">{hourlyRate}€ HT/h</span>
                        </>
                      )}
                      {includeDiscount && (
                        <>
                          <span className="text-slate-600">Remise :</span>
                          <span className="font-medium text-pink-600">-{discountPercent}%</span>
                        </>
                      )}
                    </div>
                    <div className="border-t border-emerald-200 mt-3 pt-3 flex justify-between items-center">
                      <span className="font-bold text-emerald-800">Total :</span>
                      <span className="text-2xl font-bold text-emerald-600">{budget.netAmount.toLocaleString('fr-FR')}€ HT</span>
                    </div>
                  </div>

                  {/* Payment Schedule Summary - NOT for EXPRESS */}
                  {!projectType.includes('express') && usePaymentSchedule && budget.netAmount > 0 && (
                    <div className="p-4 bg-indigo-50 rounded-lg">
                      <p className="text-sm font-medium text-indigo-700 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Échéancier de paiement ({scheduleType} échéances)
                      </p>
                      {(() => {
                        const installments = parseInt(scheduleType);
                        const deposit = (budget.netAmount * depositPercent) / 100;
                        const remaining = budget.netAmount - deposit;
                        const monthlyAmount = remaining / (installments - 1);
                        
                        return (
                          <div className="space-y-2">
                            <div className="flex justify-between items-center p-2 bg-white rounded border border-indigo-200">
                              <span className="text-sm">
                                <Badge variant="outline" className="mr-2 border-indigo-500 text-indigo-600">
                                  Acompte ({depositPercent}%)
                                </Badge>
                                À la signature
                              </span>
                              <span className="font-bold text-indigo-600">
                                {deposit.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€ HT
                              </span>
                            </div>
                            {Array.from({ length: installments - 1 }).map((_, i) => (
                              <div key={i} className="flex justify-between items-center p-2 bg-white rounded border border-slate-200">
                                <span className="text-sm text-slate-600">
                                  Échéance {i + 1} - Mois {i + 1}
                                </span>
                                <span className="font-medium text-slate-700">
                                  {monthlyAmount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€ HT
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Tasks if applicable */}
                  {budgetMode === 'tasks' && tasks.filter(t => t.name && t.hours).length > 0 && (
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm font-medium text-purple-700 mb-2">Détail des tâches</p>
                      <div className="space-y-2">
                        {tasks.filter(t => t.name && t.hours).map((task, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-slate-600">{task.name}</span>
                            <span className="font-medium">{task.hours}h = {(parseFloat(task.hours) * hourlyRate).toLocaleString('fr-FR')}€</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Site Structure Summary */}
                  {(projectType === 'website' || projectType === 'ecommerce') && sitePages.length > 0 && (
                    <div className="p-4 bg-cyan-50 rounded-lg">
                      <p className="text-sm font-medium text-cyan-700 mb-2 flex items-center gap-2">
                        <Layout className="w-4 h-4" />
                        Arborescence du site ({sitePages.reduce((acc, p) => acc + 1 + (p.children?.length || 0), 0)} pages)
                      </p>
                      <div className="space-y-1">
                        {sitePages.map((page, index) => (
                          <div key={index}>
                            <div className="flex items-center gap-2 text-sm text-cyan-800">
                              <Folder className="w-3 h-3" />
                              {page.name}
                            </div>
                            {page.children && page.children.length > 0 && (
                              <div className="ml-5 space-y-0.5">
                                {page.children.map((sub, subIndex) => (
                                  <div key={subIndex} className="flex items-center gap-2 text-xs text-cyan-600">
                                    <File className="w-3 h-3" />
                                    {sub.name}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Deliverables Summary */}
                  {deliverables.filter(d => d.checked).length > 0 && (
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Livrables inclus ({deliverables.filter(d => d.checked).length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {deliverables.filter(d => d.checked).map((item) => (
                          <Badge key={item.id} variant="secondary" className="bg-amber-100 text-amber-800">
                            <Check className="w-3 h-3 mr-1" />
                            {item.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handleSubmit(false)}
                      disabled={saving}
                      className="h-14"
                    >
                      {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
                      Enregistrer en brouillon
                    </Button>
                    <Button
                      size="lg"
                      onClick={() => handleSubmit(true)}
                      disabled={saving}
                      className="h-14 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                    >
                      {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Mail className="w-5 h-5 mr-2" />}
                      Envoyer au client pour validation
                    </Button>
                  </div>
                  <p className="text-center text-sm text-slate-500 mt-4">
                    Le client recevra un email avec le brief et le budget à valider
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
            
            {currentStep < 4 && (
              <Button onClick={nextStep} style={{ backgroundColor: theme.colors?.accent }}>
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgencyNewProjectPage;
